/**
 * Arrival dates, shown the way a Sri Lankan customer reads them.
 *
 * An ETA is either a firm day (`YYYY-MM-DD`) or just a month (`YYYY-MM`) when
 * that is all we can honestly promise. A month counts as met until its last day.
 */

export const COLOMBO_OFFSET = '+05:30'
const COLOMBO_OFFSET_MS = 5.5 * 60 * 60 * 1000

const MONTH = /^(\d{4})-(\d{2})$/

export function isMonthEta(iso: string): boolean {
  return MONTH.test(iso)
}

/** The moment an ETA is due by: midday on the day, or on the month's last day. */
export function parseEta(iso: string, now: Date = new Date()): Date | null {
  if (!iso) return null
  const month = MONTH.exec(iso)
  if (month) {
    const last = new Date(Date.UTC(Number(month[1]), Number(month[2]), 0)).getUTCDate()
    return parseEta(`${iso}-${String(last).padStart(2, '0')}`, now)
  }
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00${COLOMBO_OFFSET}` : iso)
  return Number.isNaN(d.getTime()) ? null : (void now, d)
}

export function daysUntil(iso: string, now: Date = new Date()): number | null {
  const d = parseEta(iso, now)
  if (!d) return null
  return Math.round((d.getTime() - now.getTime()) / 86_400_000)
}

export function formatEta(iso: string): string {
  const d = parseEta(iso)
  if (!d) return '—'
  return isMonthEta(iso)
    ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'Asia/Colombo' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Whole calendar months from now to a month ETA, in Colombo time. */
function monthsUntil(iso: string, now: Date): number | null {
  const m = MONTH.exec(iso)
  if (!m) return null
  const here = new Date(now.getTime() + COLOMBO_OFFSET_MS)
  return (Number(m[1]) - here.getUTCFullYear()) * 12 + (Number(m[2]) - 1 - here.getUTCMonth())
}

export function etaPhrase(iso: string, now: Date = new Date()): string {
  const months = monthsUntil(iso, now)
  if (months !== null) {
    if (months === 0) return 'this month'
    if (months === 1) return 'next month'
    if (months === -1) return 'last month'
    if (months < 0) return `${-months} months ago`
    const d = parseEta(iso, now)!
    const sameYear = d.getUTCFullYear() === new Date(now.getTime() + COLOMBO_OFFSET_MS).getUTCFullYear()
    return `in ${d.toLocaleDateString('en-GB', { month: 'long', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'Asia/Colombo' })}`
  }
  const n = daysUntil(iso, now)
  if (n === null) return '—'
  if (n > 1) return `in ${n} days`
  if (n === 1) return 'tomorrow'
  if (n === 0) return 'today'
  if (n === -1) return 'yesterday'
  return `${Math.abs(n)} days ago`
}
